import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download } from "lucide-react";
import { Panel, BrandButton } from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border border-[#E5E7EB] p-3"><div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div><div className="text-xl font-extrabold" data-testid={testId}>{value}</div></div>
);

const UploadSummary = ({ result, onClose }) => (
  <Dialog open={!!result} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent className="max-w-2xl" data-testid="master-upload-summary">
      <DialogHeader>
        <DialogTitle className="font-heading">Master list updated</DialogTitle>
        <DialogDescription>{result?.file_name}. Existing records were kept; every member account was rechecked.</DialogDescription>
      </DialogHeader>
      {result && <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Added" value={result.added} testId="master-summary-added" />
          <Stat label="Skipped" value={result.skipped} testId="master-summary-skipped" />
          <Stat label="Total on list" value={result.count} testId="master-summary-total" />
          <Stat label="Members rechecked" value={result.members_checked} testId="master-summary-members" />
        </div>
        {result.skipped_rows.length > 0 && (
          <div className="max-h-[45dvh] overflow-auto border rounded-md" data-testid="master-skipped-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FA] sticky top-0"><tr>{["Row", "GAMLS ID", "Staff ID", "Name", "Reason"].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
              <tbody>{result.skipped_rows.map((s) => (
                <tr key={s.row} className="border-t" data-testid={`master-skipped-row-${s.row}`}>
                  <td className="px-3 py-1.5">{s.row}</td><td className="px-3 py-1.5 font-mono">{s.gamls_id || "-"}</td><td className="px-3 py-1.5 font-mono">{s.staff_id || "-"}</td>
                  <td className="px-3 py-1.5">{s.name || "-"}</td><td className="px-3 py-1.5 text-amber-800">{s.reason}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
        {result.skipped > result.skipped_rows.length && <p className="text-xs text-gray-500">Showing the first {result.skipped_rows.length} skipped rows.</p>}
        <BrandButton variant="dark" onClick={onClose} data-testid="master-summary-close">Close</BrandButton>
      </>}
    </DialogContent>
  </Dialog>
);

export const MasterUpload = ({ meta, onDone }) => {
  const { user } = useApp();
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploaded_by", user.name);
    setBusy(true);
    try {
      const r = await api.post("/master-list/upload", fd);
      setResult(r.data);
      toast.success(`Master list updated: ${r.data.added} added, ${r.data.skipped} skipped`);
      setFile(null);
      ref.current.value = "";
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Panel title="Upload master list" testId="master-upload-panel"
      right={<a href={`${process.env.REACT_APP_BACKEND_URL}/api/master-list/template`} className="text-xs font-semibold text-[#E60000] hover:underline inline-flex items-center gap-1" data-testid="master-template-download"><Download className="w-3.5 h-3.5" />Template</a>}>
      <p className="text-sm text-gray-600">CSV or Excel (.xlsx) with a <strong>GAMLS ID</strong> and/or <strong>Staff ID</strong> column (Name optional). Uploading <strong>adds new people</strong> to the current list. Rows whose GAMLS ID or Staff ID is already on the list (or repeated in the file) are skipped, and every member account is rechecked.</p>
      <label className="mt-4 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-[#E60000] rounded-lg p-6 cursor-pointer transition-colors" data-testid="master-file-dropzone">
        <FileSpreadsheet className="w-8 h-8 text-[#E60000]" />
        <span className="text-sm font-medium">{file ? file.name : "Choose a .csv or .xlsx file"}</span>
        <input ref={ref} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="master-file-input" />
      </label>
      <BrandButton className="w-full mt-4 h-11" disabled={!file || busy} onClick={upload} data-testid="master-upload-button"><Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload & add to list"}</BrandButton>
      <UploadSummary result={result} onClose={() => setResult(null)} />
      {meta && (
        <div className="mt-5 text-xs text-gray-600 space-y-1 border-t pt-4" data-testid="master-meta">
          <div><span className="text-gray-400">Last file:</span> <span className="font-semibold">{meta.file_name}</span></div>
          <div><span className="text-gray-400">Records:</span> {meta.count}{meta.added != null && <> · <span className="text-gray-400">Last upload added:</span> {meta.added}</>} · <span className="text-gray-400">Skipped:</span> {meta.skipped}</div>
          <div><span className="text-gray-400">Uploaded:</span> {fmtDate(meta.uploaded_at)} by {meta.uploaded_by}</div>
          {meta.edited_at && <div data-testid="master-meta-edited"><span className="text-gray-400">Last edited:</span> {fmtDate(meta.edited_at)}{meta.edited_by ? ` by ${meta.edited_by}` : ""}</div>}
        </div>
      )}
    </Panel>
  );
};
